// Notification Context Provider - Global notification state management
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useEntity } from '@/hooks/useEntity';
import { notificationEntityConfig, type NotificationType } from '@/entities/Notification';
import { NotificationContainer } from './NotificationContainer';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface NotificationContextValue {
  notifications: NotificationType[];
  unreadCount: number;
  dismiss: (id: number) => Promise<void>;
  markRead: (id: number) => Promise<void>;
  clearAll: () => Promise<void>;
  showNotification: (notification: Omit<NotificationType, 'id' | 'created_at'>) => Promise<void>;
  reload: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [userId, setUserId] = useState<string | null>(null);
  const { items: allNotifications, create, update, delete: deleteNotification, reload } = useEntity<NotificationType>(notificationEntityConfig);
  const [sound] = useState(() => new Audio('/notification.mp3'));
  const navigate = useNavigate();

  console.log('🔔 NotificationProvider mounted, initial notifications:', allNotifications.length);

  // Get current user
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id || null);
      console.log('👤 User detected in NotificationProvider:', user?.id);
      // Force initial reload after user is detected
      if (user?.id) {
        console.log('🔄 Initial reload triggered');
        await reload();
      }
    };
    getUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Auto-refresh notifications every 30 seconds
  useEffect(() => {
    if (!userId) return;

    const pollInterval = setInterval(() => {
      console.log('🔄 Polling for new notifications...');
      reload();
    }, 30000); // 30 seconds

    return () => clearInterval(pollInterval);
  }, [userId, reload]);

  // Filter notifications for current user
  const notifications = userId 
    ? allNotifications.filter(n => n.userId === userId || n.userId === 'all')
    : [];
  const unreadCount = notifications.filter(n => !n.read && !n.acknowledged).length;

  // Debug logging
  useEffect(() => {
    console.log('🔔 Notification Provider Debug:');
    console.log('  - Current userId:', userId);
    console.log('  - Total notifications in DB:', allNotifications.length);
    console.log('  - Filtered for this user:', notifications.length);
    console.log('  - Unread count:', unreadCount);
    console.log('  - Notifications:', notifications);
  }, [userId, allNotifications.length, notifications.length, unreadCount]);

  // Debug logging
  useEffect(() => {
    console.log('📊 Notification Debug:', {
      userId,
      totalNotifications: allNotifications.length,
      filteredNotifications: notifications.length,
      unreadCount,
      notificationsList: notifications,
    });
  }, [userId, allNotifications.length, notifications.length, unreadCount]);

  // Supabase Realtime subscription for cross-browser notifications
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'entities',
          filter: `entity_type=eq.Notification`,
        },
        (payload) => {
          console.log('📢 New notification via Realtime:', payload);
          reload(); // Reload notifications when new one is created
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, reload]);

  // Poll for new notifications every 30 seconds (backup)
  useEffect(() => {
    const interval = setInterval(() => {
      reload();
    }, 30000);
    return () => clearInterval(interval);
  }, [reload]);

  // Play sound for new notifications
  useEffect(() => {
    const newUrgent = notifications.find(
      n => !n.read && !n.dismissed && (n.type === 'warning' || n.type === 'message' || n.type === 'due_date')
    );
    if (newUrgent) {
      sound.play().catch(() => {}); // Ignore autoplay errors
    }
  }, [notifications.length]);

  const dismiss = useCallback(async (id: number) => {
    const notification = notifications.find(n => n.id === id);
    if (notification) {
      await update(id, { ...notification, dismissed: true });
    }
  }, [notifications, update]);

  const markRead = useCallback(async (id: number) => {
    const notification = notifications.find(n => n.id === id);
    if (notification) {
      await update(id, { ...notification, read: true });
    }
  }, [notifications, update]);

  const clearAll = useCallback(async () => {
    await Promise.all(
      notifications
        .filter(n => !n.dismissed)
        .map(n => update(n.id, { ...n, dismissed: true }))
    );
  }, [notifications, update]);

  const showNotification = useCallback(async (notification: Omit<NotificationType, 'id' | 'created_at'>) => {
    console.log('📢 Creating notification:', notification);
    
    // Get current user if userId is 'current'
    const targetUserId = notification.userId === 'current' ? userId : notification.userId;
    
    const created = await create({
      ...notification,
      userId: targetUserId, // Use actual userId
      created_at: new Date().toISOString(),
      read: false,
      dismissed: false,
    } as any);
    console.log('✅ Notification created:', created);
    console.log('🔄 Forcing reload...');
    await reload();
    console.log('✅ Reload complete, total notifications now:', allNotifications.length);
  }, [create, reload, allNotifications.length, userId]);

  const handleNotificationClick = useCallback(async (notification: NotificationType) => {
    // Mark as read
    await markRead(notification.id);

    // Handle action
    if (notification.action_url) {
      navigate(notification.action_url);
    } else if (notification.type === 'message') {
      // Open messaging panel (you'll need to implement this)
      console.log('Open messaging panel');
    }
  }, [markRead, navigate]);

  const contextValue: NotificationContextValue = {
    notifications,
    unreadCount,
    dismiss,
    markRead,
    clearAll,
    showNotification,
    reload,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {/* Only show notifications when logged in AND not on login/signup pages */}
      {userId && !window.location.pathname.includes('/sign-in') && !window.location.pathname.includes('/sign-up') && !window.location.pathname.includes('/login') && (
        <NotificationContainer
          notifications={notifications}
          onDismiss={dismiss}
          onClick={handleNotificationClick}
        />
      )}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
